import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AuthShell } from './AuthShell'
import { FullPageLoader } from './guards'
import { Alert, Badge, Button, Spinner } from '@/components/layout/ui'
import { invitationsApi } from '@/lib/api'
import { messageFor } from '@/lib/errors'
import { formatRelative, titleCase } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { ROLE_DESCRIPTIONS } from '@/lib/permissions'
import { useSession } from '@/lib/session'

/**
 * The page an invitation link opens: `/app/invite/{token}`.
 *
 * The preview is the API's one unauthenticated route, so the workspace name and
 * role can be shown before signing in — which is what makes the sign-in prompt
 * make sense. Acceptance is always authenticated and identity-bound: the server
 * re-reads the account's email and refuses a link addressed to someone else.
 */
export default function InvitePage() {
  const { token = '' } = useParams<{ token: string }>()
  const session = useSession()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const preview = useQuery({
    queryKey: queryKeys.invitationPreview(token),
    queryFn: ({ signal }) => invitationsApi.preview(token, { signal }),
    retry: false,
    enabled: token.length > 0,
  })

  const accept = useMutation({
    mutationFn: () => invitationsApi.accept(token),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.session })
      await queryClient.invalidateQueries({ queryKey: queryKeys.invitationInbox })
      navigate(`/${result.organization.slug}`, { replace: true })
    },
  })

  const decline = useMutation({
    mutationFn: () => invitationsApi.decline(token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitationInbox })
      void preview.refetch()
    },
  })

  if (session.status === 'loading') return <FullPageLoader />

  if (preview.isPending) {
    return (
      <AuthShell title="Invitation">
        <p className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
          <Spinner className="size-4" /> Checking this invitation…
        </p>
      </AuthShell>
    )
  }

  if (preview.isError) {
    return (
      <AuthShell title="This invitation is not valid" description="The link may have expired or been revoked.">
        <Alert tone="warning">{messageFor(preview.error, 'Ask an admin to send a new invitation.')}</Alert>
        <Link
          to="/"
          className="mt-6 inline-flex h-10 items-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          Go to Taskly
        </Link>
      </AuthShell>
    )
  }

  const invitation = preview.data
  const workspace = invitation.organization

  if (invitation.status !== 'pending') {
    const copy: Record<string, string> = {
      accepted: 'This invitation has already been used.',
      revoked: 'This invitation was revoked.',
      expired: 'This invitation has expired.',
    }
    return (
      <AuthShell title={workspace.name}>
        <Alert tone="warning">
          {copy[invitation.status] ?? 'This invitation is no longer active.'}
        </Alert>
        <Link
          to="/"
          className="mt-6 inline-flex h-10 items-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          Go to Taskly
        </Link>
      </AuthShell>
    )
  }

  const signedIn = session.status === 'authenticated'
  const wrongAccount =
    signedIn &&
    session.user !== null &&
    session.user.email.toLowerCase() !== invitation.email.toLowerCase()

  return (
    <AuthShell
      title={`Join ${workspace.name}`}
      description={`You have been invited to ${workspace.name} on Taskly.`}
    >
      <dl className="rounded-card border border-ink-200 px-4 py-3 text-sm dark:border-ink-800">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-ink-500 dark:text-ink-400">Invited address</dt>
          <dd className="truncate font-medium">{invitation.email}</dd>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <dt className="text-ink-500 dark:text-ink-400">Role</dt>
          <dd>
            <Badge tone="brand">{titleCase(invitation.role)}</Badge>
          </dd>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <dt className="text-ink-500 dark:text-ink-400">Expires</dt>
          <dd className="font-medium">{formatRelative(invitation.expires_at)}</dd>
        </div>
      </dl>

      <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
        {ROLE_DESCRIPTIONS[invitation.role]}
      </p>

      {!signedIn ? (
        <div className="mt-6 space-y-3">
          <Alert tone="info">
            Sign in as <span className="font-medium">{invitation.email}</span> to accept.
          </Alert>
          <Link
            to="/login"
            state={{ from: `/invite/${token}` }}
            className="flex h-10 w-full items-center justify-center rounded-lg bg-brand-600 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            Sign in to accept
          </Link>
          <Link
            to="/register"
            state={{ from: `/invite/${token}` }}
            className="flex h-10 w-full items-center justify-center rounded-lg border border-ink-200 text-sm font-medium transition hover:bg-ink-50 dark:border-ink-700 dark:hover:bg-ink-800"
          >
            Create an account
          </Link>
        </div>
      ) : wrongAccount ? (
        <div className="mt-6 space-y-3">
          <Alert tone="warning" title="Signed in as a different account">
            This invitation was sent to {invitation.email}, but you are signed in as{' '}
            {session.user?.email}. Sign out and sign back in with the invited address.
          </Alert>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              void session.signOut().then(() => navigate('/login', { state: { from: `/invite/${token}` } }))
            }}
          >
            Sign out
          </Button>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {accept.error ? <Alert tone="danger">{messageFor(accept.error)}</Alert> : null}
          {decline.isSuccess ? <Alert tone="info">Invitation declined.</Alert> : null}
          <Button
            variant="primary"
            className="w-full"
            loading={accept.isPending}
            onClick={() => accept.mutate()}
          >
            Join {workspace.name}
          </Button>
          <Button
            className="w-full"
            loading={decline.isPending}
            disabled={accept.isPending}
            onClick={() => decline.mutate()}
          >
            Decline
          </Button>
        </div>
      )}
    </AuthShell>
  )
}
